import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Spin, Input, Select, message, Badge } from "antd";
import {
  HomeOutlined,
  ShoppingCartOutlined,
  ApartmentOutlined,
  PlusOutlined,
  MenuOutlined,
  SearchOutlined,
  FileTextOutlined,
  ThunderboltOutlined,
  MoreOutlined,
  DownOutlined,
  UserOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  BellOutlined,
  FileOutlined,
  ProfileOutlined,
  TagOutlined,
  NotificationOutlined,
  EnvironmentOutlined,
  LockOutlined,
} from "@ant-design/icons";
import api, { extractFireDACData } from "../services/api";
import Empresa from "./Empresa";
import { Notificacoes } from "./Notificacoes";
import { useCheckinNotificacoes } from "./useCheckinNotificacoes";
import { Tarefas } from "./Tarefas";
import { Passaporte } from "./Passaporte";
import { usePassaporteNotificacoes } from "./Usepassaportenotificacoes";
import Promocoes from "./Promocoes";
import NotificacoesTransferencia from "./Alertas";
import { useTransferenciaNotificacoes } from "./Usetransferencianotificacoes";
import VooHotel from "./VooHotel";
import Bloqueio from "./Bloqueio";

const DARK_NAV  = "#000000";
const ORANGE    = "#f5a623";
const SIDEBAR_W = 240;
const MOBILE_BREAKPOINT = 768;

// ── FONTE ÚNICA DE VERDADE: "quem está logado é cliente?" ───────────────────
//
// PROBLEMA QUE ISSO RESOLVE:
// O código antigo fazia `localStorage.getItem("userCliente") === "1"` inline.
// Comparação estrita com a string "1" quebra silenciosamente se o login gravar
// `true`, `"true"`, ou simplesmente não gravar a chave — e nesse caso o CLIENTE
// é tratado como GESTOR, ganhando acesso ao botão "NOVO CLIENTE".
//
// ⚠️ SE O BOTÃO AINDA APARECER PARA O CLIENTE: o problema NÃO é falta de `if`
//    no JSX (a trava `!isCliente` sempre esteve lá). É esta leitura retornando
//    false. Abra DevTools → Application → Local Storage e confira o que a tela
//    de login realmente grava em "userCliente". Note que existe também uma
//    chave parecida "userIdCliente" usada em renderPage — não confunda as duas.
const lerIsCliente = () => {
  const v = localStorage.getItem("userCliente");
  return v === "1" || v === "true" || v === "sim" || v === "S";
};

// Páginas que o perfil CLIENTE pode acessar. Lista branca (allowlist):
// tudo que não estiver aqui é negado. Nunca use lista negra aqui — página
// nova criada no futuro ficaria liberada por omissão.
const PAGINAS_CLIENTE = ["carteira", "VooHoteis"];

// ── Hook de responsividade ────────────────────────────────────────────────
// Um único ponto de verdade para "estamos em mobile ou não". Tudo que precisa
// mudar de layout entre desktop/mobile lê esse hook, em vez de espalhar
// window.innerWidth pelo código todo.
function useIsMobile(breakpoint = MOBILE_BREAKPOINT) {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= breakpoint
  );
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener("resize", handler);
    handler();
    return () => window.removeEventListener("resize", handler);
  }, [breakpoint]);
  return isMobile;
}

// ── Helper FireDAC robusto (mesmo padrão dos outros arquivos) ────────────────
const extractRows = (data) => {
  if (!data) return [];
  if (data.FDBS) {
    try {
      const mgr    = data.FDBS.Manager ?? data.FDBS.manager;
      const tables = mgr?.TableList ?? mgr?.tablelist ?? [];
      if (tables.length > 0) {
        const rowList = tables[0].RowList ?? tables[0].rowlist ?? [];
        if (rowList.length > 0)
          return rowList.map((r) => r.Original ?? r.Current ?? r);
      }
    } catch (e) {}
  }
  if (data.Table && Array.isArray(data.Table)) return data.Table;
  if (Array.isArray(data)) return data;
  return [];
};

const normalize = (row) => {
  if (!row || typeof row !== "object") return {};
  const out = {};
  for (const [k, v] of Object.entries(row)) out[k.toLowerCase()] = v;
  return out;
};

const calcularEconomia = (vendasRows) => {
  return vendasRows.reduce((acc, v) => {
    const val = Number(v.ven_veconomia ?? 0);
    return acc + (isNaN(val) ? 0 : val);
  }, 0);
};

// ─── TOPBAR ───────────────────────────────────────────────────────────────────
function Topbar({ userName, userRole, onMenuToggle }) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const items = [
    { label: "Dashboard", onClick: () => navigate("/dashboard") },
    { label: "Sair", onClick: () => { localStorage.clear(); navigate("/login"); }, danger: true },
  ];

  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 200,
      height: 56, background: DARK_NAV,
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: isMobile ? "0 12px" : "0 24px", boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
      boxSizing: "border-box",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 10 : 16, minWidth: 0 }}>
        <div onClick={onMenuToggle} style={{ cursor: "pointer", color: "#fff", fontSize: 20, display: "flex", flexShrink: 0 }}>
          <MenuOutlined />
        </div>
        {/* Logo com a MESMA fonte serifada (Cinzel) do login.
            ⚠️ Requer o <link> do Google Fonts no public/index.html — sem ele,
            cai num serif genérico. Só o "GVS Gestão" muda; o resto continua igual. */}
        <span style={{ fontFamily: "'Cinzel', serif", fontSize: isMobile ? 16 : 21, fontWeight: 600, color: "#fff", letterSpacing: "1px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          GVS<span style={{ color: "#fff" }}></span>
        </span>
      </div>

      <div ref={ref} style={{ position: "relative", flexShrink: 0 }}>
        <div onClick={() => setOpen(v => !v)}
          style={{ display: "flex", alignItems: "center", gap: isMobile ? 6 : 10, cursor: "pointer", padding: "6px 8px", borderRadius: 6, background: open ? "rgba(255,255,255,0.08)" : "transparent" }}>
          <div style={{ width: 34, height: 34, borderRadius: "50%", background: "#4a6a7a", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, fontWeight: 700, color: "#ccc", flexShrink: 0 }}>
            {(userName || "U").charAt(0).toUpperCase()}
          </div>
          {!isMobile && (
            <div style={{ lineHeight: 1.3 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#fff" }}>{userName}</div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.6)" }}>{userRole || "coordenador"}</div>
            </div>
          )}
        </div>
        {open && (
          <div style={{ position: "absolute", top: "calc(100% + 6px)", right: 0, background: "#fff", borderRadius: 8, boxShadow: "0 4px 20px rgba(0,0,0,0.15)", minWidth: 160, overflow: "hidden", zIndex: 300 }}>
            {isMobile && (
              <div style={{ padding: "12px 20px", borderBottom: "1px solid #f0f0f0" }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#333" }}>{userName}</div>
                <div style={{ fontSize: 11, color: "#999" }}>{userRole || "coordenador"}</div>
              </div>
            )}
            {items.map((item, i) => (
              <div key={i} onClick={() => { setOpen(false); item.onClick(); }}
                style={{ padding: "12px 20px", fontSize: 14, color: item.danger ? "#cf1322" : "#333", cursor: "pointer", borderBottom: i < items.length - 1 ? "1px solid #f0f0f0" : "none" }}
                onMouseEnter={e => e.currentTarget.style.background = "#f5f5f5"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                {item.label}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── SIDEBAR ──────────────────────────────────────────────────────────────────
const getNavItems = (isCliente) => {
  // Se for cliente, mostra Carteira e Voos e Hotéis
  if (isCliente) {
    return [
      { key: "carteira", icon: <ShoppingCartOutlined />, label: "Carteira" },
      { key: "VooHoteis", icon: <EnvironmentOutlined />, label: "Voos e Hotéis" },
    ];
  }

  return [
    { key: "gestores", icon: <HomeOutlined />, label: "Gestores" },
    { key: "carteira", icon: <ShoppingCartOutlined />, label: "Carteira" },
    { key: "dados-empresa", icon: <ApartmentOutlined />, label: "Dados da Empresa" },
    { key: "notificacoes", icon: <BellOutlined />, label: "Check-in" },
    { key: "tarefas", icon: <FileOutlined />, label: "Tarefas" },
    { key: "passaporte", icon: <ProfileOutlined />, label: "Passaportes" },
    { key: "bloqueios", icon: <LockOutlined />, label: "Bloqueios" },
    { key: "promocoes", icon: <TagOutlined />, label: "Promoções" },
    { key: "VooHoteis", icon: <EnvironmentOutlined />, label: "Voos e Hotéis" },
    { key: "alertas", icon: <NotificationOutlined />, label: "Alertas" }
  ];
};

function Sidebar({ current, onChange, visible, badges = {}, isCliente, isMobile, onRequestClose }) {
  const items = getNavItems(isCliente);

  const handleSelect = (key) => {
    onChange(key);
    // No mobile a sidebar é um overlay: some sozinha depois de escolher a página
    if (isMobile) onRequestClose();
  };

  return (
    <>
      {/* Backdrop: só existe no mobile, e só quando a sidebar está aberta.
          Clicar fora fecha a sidebar — comportamento padrão de drawer mobile. */}
      {isMobile && visible && (
        <div
          onClick={onRequestClose}
          style={{ position: "fixed", top: 56, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.45)", zIndex: 99 }}
        />
      )}
      <div style={{
        position: "fixed", top: 56, left: visible ? 0 : -SIDEBAR_W,
        width: SIDEBAR_W, maxWidth: "82vw", height: "calc(100vh - 56px)",
        background: "#fff", borderRight: "1px solid #e8e8e8",
        zIndex: 100, transition: "left 0.25s ease",
        display: "flex", flexDirection: "column", paddingTop: 8,
        boxShadow: isMobile && visible ? "2px 0 16px rgba(0,0,0,0.2)" : "none",
        boxSizing: "border-box",
      }}>
        {items.map(item => {
          const isActive = current === item.key;
          const badge    = badges[item.key];
          return (
            <div key={item.key} onClick={() => handleSelect(item.key)}
              style={{
                display: "flex", alignItems: "center", gap: 12,
                padding: "13px 20px", cursor: "pointer", fontSize: 14,
                fontWeight: isActive ? 600 : 400,
                color: isActive ? DARK_NAV : "#555",
                background: isActive ? "#f0f4f7" : "transparent",
                borderLeft: isActive ? `3px solid ${ORANGE}` : "3px solid transparent",
                transition: "all 0.15s",
              }}
              onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = "#f7f8fa"; }}
              onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = "transparent"; }}>
              <Badge count={badge?.count || 0} color={badge?.color} size="small" offset={[4, -2]}>
                <span style={{ fontSize: 16, color: isActive ? DARK_NAV : "#555" }}>{item.icon}</span>
              </Badge>
              {item.label}
            </div>
          );
        })}
      </div>
    </>
  );
}

// ─── MODAL DETALHES ───────────────────────────────────────────────────────────
function ModalDetalhes({ cliente, onClose }) {
  const isMobile = useIsMobile();
  if (!cliente) return null;
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", padding: isMobile ? 12 : 0, boxSizing: "border-box" }} onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: 12, width: isMobile ? "100%" : 520, maxWidth: "95vw", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 8px 40px rgba(0,0,0,0.18)" }} onClick={e => e.stopPropagation()}>
        <div style={{ background: DARK_NAV, padding: isMobile ? "16px 18px" : "20px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
            <div style={{ width: 44, height: 44, borderRadius: "50%", background: ORANGE, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
              {(cliente.nome || "C").charAt(0).toUpperCase()}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{cliente.nome}</div>
              <div style={{ fontSize: 12, color: "rgba(255,255,255,0.6)" }}>Detalhes do Assinante</div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: "rgba(255,255,255,0.12)", border: "none", borderRadius: 6, color: "#fff", fontSize: 18, cursor: "pointer", width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>×</button>
        </div>
        <div style={{ padding: isMobile ? "18px" : "24px" }}>
          {[
            { label: "NOME COMPLETO", value: cliente.nome },
            { label: "E-MAIL",        value: cliente.email },
            { label: "CONTATO",       value: cliente.contato || "—" },
            { label: "PLANO",         value: cliente.plano || "GESTAO" },
            { label: "GESTOR",        value: cliente.gestor || "—" },
          ].map((field, i) => (
            <div key={i} style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: isMobile ? 4 : 0, justifyContent: "space-between", alignItems: isMobile ? "flex-start" : "center", padding: "12px 0", borderBottom: i < 4 ? "1px solid #f0f0f0" : "none" }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: "#999", letterSpacing: "0.5px" }}>{field.label}</span>
              <span style={{ fontSize: 14, color: "#333", fontWeight: 500, textAlign: isMobile ? "left" : "right", maxWidth: isMobile ? "100%" : "60%", wordBreak: "break-word" }}>{field.value}</span>
            </div>
          ))}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0" }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: "#999", letterSpacing: "0.5px" }}>STATUS ASSINATURA</span>
            <span style={{ background: cliente.assinatura === "ATIVA" ? "#f6ffed" : "#fff7e6", border: `1px solid ${cliente.assinatura === "ATIVA" ? "#b7eb8f" : "#ffd591"}`, color: cliente.assinatura === "ATIVA" ? "#389e0d" : "#d46b08", borderRadius: 4, padding: "3px 12px", fontSize: 12, fontWeight: 600 }}>
              {cliente.assinatura || "ATIVA"}
            </span>
          </div>
          <div style={{ marginTop: 16, background: "#f6ffed", border: "1px solid #b7eb8f", borderRadius: 8, padding: "14px 16px", display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: "#389e0d" }}>💰 Economia Apurada</span>
            <span style={{ fontSize: 16, fontWeight: 700, color: "#389e0d" }}>
              R$ {Number(cliente.economiaApurada || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
            </span>
          </div>
        </div>
        <div style={{ padding: isMobile ? "14px 18px" : "16px 24px", borderTop: "1px solid #f0f0f0", display: "flex", justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ padding: "8px 20px", background: "#fff", border: "1px solid #d9d9d9", borderRadius: 6, cursor: "pointer", fontSize: 14, color: "#555" }}>Fechar</button>
        </div>
      </div>
    </div>
  );
}

// ─── MODAL NOVO CLIENTE ───────────────────────────────────────────────────────
function ModalNovoCliente({ userId, gestores, onClose, onSuccess }) {
  const isMobile = useIsMobile();
  const [form, setForm] = useState({ nome: "", sobrenome: "", email: "", celular: "", cpf: "", login: "", senha: "", plano: "", gestor: "" });
  const [showSenha, setShowSenha] = useState(false);
  const [loading, setLoading]     = useState(false);
  const [emailError, setEmailError] = useState("");

  const validarEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (field === "email") setEmailError(value && !validarEmail(value) ? "Email inválido." : "");
  };

  const handleSubmit = async () => {
    // Trava redundante de propósito: mesmo que alguém renderize este modal
    // por engano num contexto de cliente, o POST não sai daqui.
    // ⚠️ Isso NÃO é segurança — veja a nota no topo de CarteiraPage.
    if (lerIsCliente()) {
      message.error("Seu perfil não permite cadastrar clientes.");
      return;
    }
    if (!form.nome || !form.email) { message.error("Nome e e-mail são obrigatórios."); return; }
    if (emailError) { message.error("Corrija o e-mail antes de continuar."); return; }
    setLoading(true);
    try {
      const nomeCompleto = [form.nome, form.sobrenome].filter(Boolean).join(" ");
      await api.post("/ServerPrincipal/InserirClientes", {
        Login: String(form.gestor || userId),
        Nome: nomeCompleto, CPF: form.cpf, Email: form.email,
        Telefone: form.celular, IDCliente: form.gestor || 0,
        Senha: form.senha, LogLogin: form.login || form.email,
      });
      message.success("Cliente cadastrado com sucesso!");
      onSuccess(); onClose();
    } catch (err) {
      message.error("Erro ao cadastrar cliente."); console.error(err);
    } finally { setLoading(false); }
  };

  const labelStyle  = { fontSize: 12, color: "#666", marginBottom: 4, display: "block" };
  const inputStyle  = { width: "100%", padding: "8px 10px", border: "1px solid #d9d9d9", borderRadius: 6, fontSize: 14, outline: "none", boxSizing: "border-box", color: "#333" };
  const sectionTitle = { fontSize: 13, fontWeight: 600, color: "#888", marginBottom: 14, marginTop: 20, paddingBottom: 8, borderBottom: "1px solid #f0f0f0" };
  const gridCols = { display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: isMobile ? 0 : "0 16px" };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", padding: isMobile ? 12 : 0, boxSizing: "border-box" }} onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: 12, width: isMobile ? "100%" : 540, maxWidth: "95vw", boxShadow: "0 8px 40px rgba(0,0,0,0.18)", overflow: "hidden", maxHeight: "92vh", display: "flex", flexDirection: "column" }} onClick={e => e.stopPropagation()}>
        <div style={{ background: DARK_NAV, padding: isMobile ? "16px 18px" : "18px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: "#fff" }}>Nova assinatura</span>
          <button onClick={onClose} style={{ background: "rgba(255,255,255,0.12)", border: "none", borderRadius: 6, color: "#fff", fontSize: 18, cursor: "pointer", width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
        </div>
        <div style={{ padding: isMobile ? "18px" : "20px 24px", overflowY: "auto", flex: 1 }}>
          <div style={sectionTitle}>Dados do assinante</div>
          <div style={gridCols}>
            <div style={{ marginBottom: 14 }}><label style={labelStyle}>Nome</label><input style={inputStyle} placeholder="Nome do gerido" value={form.nome} onChange={e => handleChange("nome", e.target.value)} /></div>
            <div style={{ marginBottom: 14 }}><label style={labelStyle}>Sobrenome</label><input style={inputStyle} placeholder="Sobrenome do gerido" value={form.sobrenome} onChange={e => handleChange("sobrenome", e.target.value)} /></div>
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Email</label>
            <input style={{ ...inputStyle, borderColor: emailError ? "#ff4d4f" : "#d9d9d9" }} placeholder="email@exemplo.com" value={form.email} onChange={e => handleChange("email", e.target.value)} />
            {emailError && <div style={{ color: "#ff4d4f", fontSize: 12, marginTop: 4 }}>{emailError}</div>}
          </div>
          <div style={gridCols}>
            <div style={{ marginBottom: 14 }}><label style={labelStyle}>Celular</label><input style={inputStyle} placeholder="(xx) xxxxx-xxxxx" value={form.celular} onChange={e => handleChange("celular", e.target.value)} /></div>
            <div style={{ marginBottom: 14 }}><label style={labelStyle}>CPF</label><input style={inputStyle} placeholder="xxx.xxx.xxx-xx" value={form.cpf} onChange={e => handleChange("cpf", e.target.value)} /></div>
          </div>
          <div style={sectionTitle}>Acesso ao sistema</div>
          <div style={gridCols}>
            <div style={{ marginBottom: 14 }}><label style={labelStyle}>Login (Nome Usuário)</label><input style={inputStyle} placeholder="Nome usado para login" value={form.login} onChange={e => handleChange("login", e.target.value)} /></div>
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Senha</label>
              <div style={{ position: "relative" }}>
                <input type={showSenha ? "text" : "password"} style={{ ...inputStyle, paddingRight: 36 }} placeholder="••••••••" value={form.senha} onChange={e => handleChange("senha", e.target.value)} />
                <span onClick={() => setShowSenha(v => !v)} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", cursor: "pointer", color: "#aaa", fontSize: 16 }}>
                  {showSenha ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                </span>
              </div>
            </div>
          </div>
          <div style={sectionTitle}>Dados da assinatura</div>
          <div style={{ ...gridCols, marginBottom: 14 }}>
            <div style={{ marginBottom: isMobile ? 14 : 0 }}>
              <label style={labelStyle}>Plano</label>
              <select style={{ ...inputStyle, cursor: "pointer", background: "#fff" }} value={form.plano} onChange={e => handleChange("plano", e.target.value)}>
                <option value="">Selecione...</option>
                <option value="GESTAO">Gestão</option>
                <option value="BASICO">Básico</option>
                <option value="PREMIUM">Premium</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Gestor</label>
              <select style={{ ...inputStyle, cursor: "pointer", background: "#fff" }} value={form.gestor} onChange={e => handleChange("gestor", e.target.value)}>
                <option value="">Selecione o gestor...</option>
                {gestores.map(g => <option key={g.id} value={g.id}>{g.nome}</option>)}
              </select>
            </div>
          </div>
          <div style={{ background: "#fffbe6", border: "1px solid #ffe58f", borderRadius: 8, padding: "12px 16px", marginTop: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#d48806", marginBottom: 4 }}>💳 Forma de Pagamento</div>
            <div style={{ fontSize: 12, color: "#ad6800", lineHeight: 1.6 }}>A cobrança da assinatura será realizada no cartão cadastrado da empresa. Se o gateway de pagamento indicar falha na cobrança, você ainda poderá pagar a assinatura através da fatura.</div>
          </div>
        </div>
        <div style={{ padding: isMobile ? "14px 18px" : "16px 24px", borderTop: "1px solid #f0f0f0", display: "flex", justifyContent: "flex-end", gap: 10, flexShrink: 0 }}>
          <button onClick={onClose} style={{ padding: "8px 20px", background: "#fff", border: "1px solid #d9d9d9", borderRadius: 6, cursor: "pointer", fontSize: 14, color: "#555" }}>Cancelar</button>
          <button onClick={handleSubmit} disabled={loading || !!emailError || !form.nome || !form.email}
            style={{ padding: "8px 24px", background: loading || !form.nome || !form.email || emailError ? "#ccc" : DARK_NAV, color: "#fff", border: "none", borderRadius: 6, cursor: loading || !form.nome || !form.email || emailError ? "not-allowed" : "pointer", fontSize: 14, fontWeight: 600 }}>
            {loading ? "Cadastrando..." : "CADASTRAR"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── MODAL EDITAR CLIENTE ─────────────────────────────────────────────────────
function ModalEditarCliente({ cliente, onClose, onSuccess }) {
  const isMobile = useIsMobile();
  const [form, setForm] = useState({
    nome:    cliente?.nome    || "",
    cpf:     cliente?.cpf     || "",
    email:   cliente?.email   || "",
    celular: cliente?.contato && cliente.contato !== "—" ? cliente.contato : "",
  });
  const [loading, setLoading]       = useState(false);
  const [emailError, setEmailError] = useState("");

  const validarEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (field === "email") setEmailError(value && !validarEmail(value) ? "Email inválido." : "");
  };

  const handleSubmit = async () => {
    // Mesma trava redundante do ModalNovoCliente.
    if (lerIsCliente()) {
      message.error("Seu perfil não permite editar assinantes.");
      return;
    }
    if (!form.nome || !form.email) { message.error("Nome e e-mail são obrigatórios."); return; }
    if (emailError) { message.error("Corrija o e-mail antes de continuar."); return; }
    setLoading(true);
    try {
      await api.post("/ServerPrincipal/AtualizarTitular", {
        Nome:     form.nome,
        CPF:      form.cpf,
        Email:    form.email,
        Telefone: form.celular,
        Cliente:  Number(cliente.id),
      });
      message.success("Cliente atualizado com sucesso!");
      onSuccess(); onClose();
    } catch (err) {
      message.error("Erro ao atualizar cliente."); console.error(err);
    } finally { setLoading(false); }
  };

  const labelStyle = { fontSize: 12, color: "#666", marginBottom: 4, display: "block" };
  const inputStyle = { width: "100%", padding: "8px 10px", border: "1px solid #d9d9d9", borderRadius: 6, fontSize: 14, outline: "none", boxSizing: "border-box", color: "#333" };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", padding: isMobile ? 12 : 0, boxSizing: "border-box" }} onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: 12, width: isMobile ? "100%" : 540, maxWidth: "95vw", boxShadow: "0 8px 40px rgba(0,0,0,0.18)", overflow: "hidden", maxHeight: "92vh", display: "flex", flexDirection: "column" }} onClick={e => e.stopPropagation()}>
        <div style={{ background: DARK_NAV, padding: isMobile ? "16px 18px" : "18px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: "#fff" }}>Editar assinante</span>
          <button onClick={onClose} style={{ background: "rgba(255,255,255,0.12)", border: "none", borderRadius: 6, color: "#fff", fontSize: 18, cursor: "pointer", width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
        </div>
        <div style={{ padding: isMobile ? "18px" : "20px 24px", overflowY: "auto", flex: 1 }}>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Nome completo</label>
            <input style={inputStyle} placeholder="Nome do assinante" value={form.nome} onChange={e => handleChange("nome", e.target.value)} />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Email</label>
            <input style={{ ...inputStyle, borderColor: emailError ? "#ff4d4f" : "#d9d9d9" }} placeholder="email@exemplo.com" value={form.email} onChange={e => handleChange("email", e.target.value)} />
            {emailError && <div style={{ color: "#ff4d4f", fontSize: 12, marginTop: 4 }}>{emailError}</div>}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: isMobile ? 0 : "0 16px" }}>
            <div style={{ marginBottom: 14 }}><label style={labelStyle}>Celular</label><input style={inputStyle} placeholder="(xx) xxxxx-xxxxx" value={form.celular} onChange={e => handleChange("celular", e.target.value)} /></div>
            <div style={{ marginBottom: 14 }}><label style={labelStyle}>CPF</label><input style={inputStyle} placeholder="xxx.xxx.xxx-xx" value={form.cpf} onChange={e => handleChange("cpf", e.target.value)} /></div>
          </div>
        </div>
        <div style={{ padding: isMobile ? "14px 18px" : "16px 24px", borderTop: "1px solid #f0f0f0", display: "flex", justifyContent: "flex-end", gap: 10, flexShrink: 0 }}>
          <button onClick={onClose} style={{ padding: "8px 20px", background: "#fff", border: "1px solid #d9d9d9", borderRadius: 6, cursor: "pointer", fontSize: 14, color: "#555" }}>Cancelar</button>
          <button onClick={handleSubmit} disabled={loading || !!emailError || !form.nome || !form.email}
            style={{ padding: "8px 24px", background: loading || !form.nome || !form.email || emailError ? "#ccc" : DARK_NAV, color: "#fff", border: "none", borderRadius: 6, cursor: loading || !form.nome || !form.email || emailError ? "not-allowed" : "pointer", fontSize: 14, fontWeight: 600 }}>
            {loading ? "Salvando..." : "SALVAR ALTERAÇÕES"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── PÁGINA GESTORES ──────────────────────────────────────────────────────────
function GestoresPage({ userId }) {
  const isMobile = useIsMobile();
  const [gestores, setGestores]     = useState([]);
  const [loading, setLoading]       = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [gestorDetalhes, setGestorDetalhes] = useState(null);
  const menuRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    api.get("/ServerPrincipal/PesquisaGestor", { params: { Login: userId } })
      .then(r => {
        const data = extractFireDACData(r.data);
        setGestores(data.map((g, i) => ({
          key: i.toString(),
          id:      g.log_id      || g.id,
          nome:    g.log_nome    || g.nome,
          email:   g.log_email   || g.email,
          contato: g.log_contato || g.telefone || "—",
          status:  g.log_ativo === 1 || g.log_ativo === "1" ? "Ativo" : (g.status || "Ativo"),
        })));
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [userId]);

  useEffect(() => {
    const h = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setOpenMenuId(null); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const COLS = "2fr 2.5fr 1.5fr 1fr 60px";
  const th   = { fontSize: 12, fontWeight: 600, color: "#888" };

  const StatusBadge = ({ status }) => (
    <span style={{ background: status === "Ativo" ? "#f6ffed" : "#fff1f0", border: `1px solid ${status === "Ativo" ? "#b7eb8f" : "#ffa39e"}`, color: status === "Ativo" ? "#389e0d" : "#cf1322", borderRadius: 4, padding: "2px 8px", fontSize: 12, fontWeight: 600 }}>
      {status === "Ativo" ? "Ativo" : "Inativo"}
    </span>
  );

  return (
    <div>
      {gestorDetalhes && <ModalDetalhes cliente={{ ...gestorDetalhes, assinatura: gestorDetalhes.status }} onClose={() => setGestorDetalhes(null)} />}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <HomeOutlined style={{ fontSize: 18, color: "#555" }} />
          <span style={{ fontSize: 18, fontWeight: 600, color: "#222" }}>Gestores</span>
        </div>
        <button onClick={() => navigate("/cadastro-gestor")}
          style={{ display: "flex", alignItems: "center", gap: 6, background: DARK_NAV, color: "#fff", border: "none", borderRadius: 6, padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
          <PlusOutlined style={{ fontSize: 12 }} /> NOVO GESTOR
        </button>
      </div>

      <div style={{ background: "#fff", borderRadius: 8, border: "1px solid #e8e8e8", overflow: isMobile ? "visible" : "auto" }}>
        {!isMobile && (
          <div style={{ display: "grid", gridTemplateColumns: COLS, padding: "10px 20px", background: "#fafafa", borderBottom: "1px solid #f0f0f0", minWidth: 600 }}>
            {["Gestor", "Email", "Contato", "Status", ""].map((h, i) => <div key={i} style={th}>{h}</div>)}
          </div>
        )}
        <Spin spinning={loading}>
          {gestores.length === 0 && !loading ? (
            <div style={{ textAlign: "center", padding: "48px 0", color: "#bbb" }}>Nenhum gestor encontrado</div>
          ) : isMobile ? (
            // ── Layout em cards no mobile: cada gestor vira um bloco empilhado,
            // em vez de forçar 5 colunas numa tela de 375px ──
            gestores.map((g) => (
              <div key={g.key} style={{ padding: "14px 16px", borderBottom: "1px solid #f5f5f5", position: "relative" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: "#333" }}>{g.nome}</div>
                    <div style={{ fontSize: 12, color: "#666", marginTop: 3, wordBreak: "break-all" }}>{g.email}</div>
                    <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>{g.contato}</div>
                    <div style={{ marginTop: 8 }}><StatusBadge status={g.status} /></div>
                  </div>
                  <div style={{ cursor: "pointer", padding: 6, borderRadius: 4, background: openMenuId === g.key ? "#f0f0f0" : "transparent", flexShrink: 0 }}
                    onClick={(e) => { e.stopPropagation(); setOpenMenuId(openMenuId === g.key ? null : g.key); }}>
                    <MoreOutlined style={{ fontSize: 18, color: "#555" }} />
                  </div>
                </div>
                {openMenuId === g.key && (
                  <div ref={menuRef} style={{ position: "absolute", right: 16, top: 44, background: "#fff", borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.15)", border: "1px solid #e8e8e8", zIndex: 300, minWidth: 140, overflow: "hidden" }}>
                    <button onClick={() => { setOpenMenuId(null); setGestorDetalhes(g); }}
                      style={{ width: "100%", padding: "12px 16px", border: "none", background: "#fff", textAlign: "left", fontSize: 14, color: "#333", cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 16 }}>📋</span> Detalhes
                    </button>
                  </div>
                )}
              </div>
            ))
          ) : (
            gestores.map((g, idx) => (
              <div key={g.key}
                style={{ display: "grid", gridTemplateColumns: COLS, padding: "13px 20px", borderBottom: "1px solid #f5f5f5", alignItems: "center", background: idx % 2 === 0 ? "#fff" : "#fafcff", position: "relative", minWidth: 600 }}
                onMouseEnter={e => e.currentTarget.style.background = "#f0f7ff"}
                onMouseLeave={e => e.currentTarget.style.background = idx % 2 === 0 ? "#fff" : "#fafcff"}>
                <div style={{ fontSize: 13, fontWeight: 500, color: "#333" }}>{g.nome}</div>
                <div style={{ fontSize: 13, color: "#666", wordBreak: "break-all" }}>{g.email}</div>
                <div style={{ fontSize: 13, color: "#666" }}>{g.contato}</div>
                <div><StatusBadge status={g.status} /></div>
                <div style={{ display: "flex", justifyContent: "center", position: "relative" }}>
                  <div style={{ cursor: "pointer", padding: "8px", borderRadius: "4px", display: "flex", alignItems: "center", justifyContent: "center", background: openMenuId === g.key ? "#f0f0f0" : "transparent" }}
                    onClick={(e) => { e.stopPropagation(); setOpenMenuId(openMenuId === g.key ? null : g.key); }}>
                    <MoreOutlined style={{ fontSize: 18, color: "#555" }} />
                  </div>
                  {openMenuId === g.key && (
                    <div ref={menuRef} style={{ position: "fixed", right: "20px", marginTop: "8px", background: "#fff", borderRadius: "8px", boxShadow: "0 4px 12px rgba(0,0,0,0.15)", border: "1px solid #e8e8e8", zIndex: 9999, minWidth: "140px", overflow: "hidden" }}>
                      <button onClick={() => { setOpenMenuId(null); setGestorDetalhes(g); }}
                        style={{ width: "100%", padding: "12px 16px", border: "none", background: "#fff", textAlign: "left", fontSize: "14px", color: "#333", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px" }}
                        onMouseEnter={e => e.currentTarget.style.background = "#f5f5f5"}
                        onMouseLeave={e => e.currentTarget.style.background = "#fff"}>
                        <span style={{ fontSize: "16px" }}>📋</span> Detalhes
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </Spin>
      </div>
    </div>
  );
}

// ─── PÁGINA CARTEIRA ──────────────────────────────────────────────────────────
function CarteiraPage({ userId, isCliente = true }) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [clientes, setClientes]               = useState([]);
  const [gestores, setGestores]               = useState([]);
  const [loading, setLoading]                 = useState(false);
  const [filtroAberto, setFiltroAberto]       = useState(true);
  const [searchTerm, setSearchTerm]           = useState("");
  const [filtroStatus, setFiltroStatus]       = useState(null);
  const [filtroPlano, setFiltroPlano]         = useState(null);
  const [gestorExpandido, setGestorExpandido] = useState({});
  const [clienteSelecionado, setClienteSelecionado] = useState(null);
  const [showNovoCliente, setShowNovoCliente] = useState(false);
  const [openMenuClienteId, setOpenMenuClienteId] = useState(null);
  const [menuPos, setMenuPos]                 = useState({ top: 0, left: 0 });
  const [clienteEditar, setClienteEditar]     = useState(null);
  const [nomeGestorLogado, setNomeGestorLogado]   = useState("");
  const [compensacaoData, setCompensacaoData]     = useState({});
  const menuRef = useRef(null);


  const abrirNovoCliente = () => {
    if (isCliente) return;
    setShowNovoCliente(true);
  };

  const abrirEditarCliente = (c) => {
    if (isCliente) return;
    setClienteEditar(c);
  };

  // ─── FUNÇÃO PARA NORMALIZAR DADOS (lowercase das chaves) ──────────────────
  const normalize = (obj) => {
    const newObj = {};
    Object.keys(obj).forEach(key => {
      newObj[key.toLowerCase()] = obj[key];
    });
    return newObj;
  };

  // ─── FUNÇÃO PARA NORMALIZAR HOSPEDAGEM ────────────────────────────────────
  const normalizaHospedagem = (h) => {
    return {
      tra_categoria: 'HOSPEDAGEM',
      ven_origem: h.hos_nome_hospedagem || h.nome_hospedagem || '',
      ven_data_voo: h.hos_data_check_in || h.data_check_in || '',
      tra_data: h.hos_data_check_in || h.data_check_in || '',
      tra_vtotal: Number(h.hos_vreserva || h.vreserva || 0),
      ven_qtde_milhas: Number(h.hos_qtde_milhas || h.qtde_milhas || 0),
      tra_vunitario: Number(h.hos_vunitario || h.vunitario || 0),
      ven_taxas: Number(h.hos_taxas || h.taxas || 0),
      ven_veconomia: Number(h.hos_veconomia || h.veconomia || 0),
      ven_peconomia: Number(h.hos_peconomia || h.peconomia || 0),
      hos_voucher_reserva: h.hos_voucher_reserva || h.voucher_reserva || '',
      hos_data_check_in: h.hos_data_check_in || h.data_check_in || '',
      hos_data_check_out: h.hos_data_check_out || h.data_check_out || '',
      hos_nome_hospedagem: h.hos_nome_hospedagem || h.nome_hospedagem || '',
    };
  };

   // ─── FONTE ÚNICA DE CÁLCULO DE ECONOMIA ────────────────────────────────────
  // Mesma lógica usada no TabUtilizacao
  const calcularEconomiaLinha = (row) => {
    const econBanco = Number(row.ven_veconomia || row.ven_economia || row.veconomia || 0);
    if (econBanco > 0) return econBanco;

    const milhas    = Number(row.ven_qtde_milhas || row.qtde_milhas || 0);
    const custoUnit = Number(row.tra_vunitario   || row.vunitario   || 0);
    if (milhas <= 0 || custoUnit <= 0) return 0;

    const valorTotal  = Number(row.tra_vtotal || 0);
    const taxas       = Number(row.ven_taxas || row.taxas || 0);
    const valorMilhas = (milhas * custoUnit) / 1000;

    return Math.max(0, valorTotal - (valorMilhas + taxas));
  };

  // ── CARREGA DADOS ──────────────────────────────────────────────────────────
  const carregarDados = () => {
    if (!userId) return;
    setLoading(true);

    // 1. Busca dados do gestor logado
    api.get("/ServerPrincipal/PesquisaGestor", { params: { Login: userId } })
      .then(r => {
        const data = extractFireDACData(r.data);
        if (data && data.length > 0) setNomeGestorLogado(data[0].log_nome || data[0].nome || "Sem Gestor");
        setGestores(data.map(g => ({ id: g.log_id || g.id, nome: g.log_nome || g.nome })));
      })
      .catch(console.error);

    // 2. Busca lista de clientes
    api.get("/ServerPrincipal/PesquisaClientes", { params: { Login: userId } })
      .then(async r => {
        const data = extractFireDACData(r.data);

        // Formata clientes com economia zerada por enquanto
        const formatted = data.map((c, i) => ({
          key:             i.toString(),
          id:              c.cli_id       || c.id,
          nome:            c.cli_nome     || c.nome,
          email:           c.cli_email    || c.email,
          economiaApurada: 0,
          plano:           c.cli_plano    || c.plano    || "GESTAO",
          status:          c.cli_status   || c.status   || "Ativo",
          assinatura:      c.cli_assinatura || c.assinatura || "ATIVA",
          contato:         c.cli_telefone || c.telefone || "—",
          cpf:             c.cli_cpf      || c.cpf      || "",
          pendencias:      c.cli_pendencias || c.pendencias || 0,
        }));

        setClientes(formatted);

        // 3. Para cada cliente, busca a economia via /PesquisaUltilizacao E /PesquisaHospedagem
        await Promise.all(
          formatted.map(async (cliente) => {
            let todasLinhas = [];
            let dataUtilizacao = [];
            let dataHospedagem = [];

            try {
              // Busca utilizações (vendas/voos)
              const resUtilizacao = await api.get("/ServerPrincipal/PesquisaUltilizacao", { params: { Cliente: cliente.id } });
              dataUtilizacao = extractFireDACData(resUtilizacao.data).map(normalize);
              todasLinhas = [...dataUtilizacao];
            } catch (e) {
              console.warn(`[Economia] Erro ao buscar utilização do cliente ${cliente.id}:`, e);
            }

            try {
              // Busca hospedagens - CORREÇÃO AQUI!
              const resHospedagem = await api.get("/ServerPrincipal/PesquisaHospedagem", { params: { Cliente: cliente.id } })
                .catch((e) => {
                  console.warn(`[Economia] Falha ao buscar hospedagem do cliente ${cliente.id}:`, e);
                  return { data: [] };
                });

              dataHospedagem = extractFireDACData(resHospedagem.data).map(normalize).map(normalizaHospedagem);
              todasLinhas = [...todasLinhas, ...dataHospedagem];

              console.log(`Cliente ${cliente.id} - ${cliente.nome}: ${dataUtilizacao.length} utilizações + ${dataHospedagem.length} hospedagens = ${todasLinhas.length} total`);
            } catch (e) {
              console.warn(`[Economia] Erro ao processar hospedagem do cliente ${cliente.id}:`, e);
            }

            // Calcula o total de economia com todas as linhas (vendas + hospedagens)
            const totalEconomia = todasLinhas.reduce((s, row) => s + calcularEconomiaLinha(row), 0);

            // Log detalhado para debug
            if (dataHospedagem.length > 0) {
              const somaVendas = dataUtilizacao.reduce((s, r) => s + calcularEconomiaLinha(r), 0);
              const somaHospedagens = dataHospedagem.reduce((s, r) => s + calcularEconomiaLinha(r), 0);
              console.log(`[${cliente.nome}] Vendas: R$ ${somaVendas.toFixed(2)} | Hospedagens: R$ ${somaHospedagens.toFixed(2)} | Total: R$ ${totalEconomia.toFixed(2)}`);
            }

            // Atualiza o campo economiaApurada deste cliente
            setClientes(prev =>
              prev.map(c =>
                c.id === cliente.id ? { ...c, economiaApurada: totalEconomia } : c
              )
            );

            // Busca compensação (inalterado)
            try {
              const compensaRes  = await api.get("/ServerPrincipal/PesquisaCompensa", { params: { Cliente: cliente.id } });
              const compensaData = extractFireDACData(compensaRes.data);
              if (compensaData && compensaData.length > 0) {
                setCompensacaoData(prev => ({ ...prev, [cliente.id]: compensaData }));
              }
            } catch (e) {
              console.warn(`[Compensação] Erro cliente ${cliente.id}:`, e);
            }
          })
        );
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { carregarDados(); }, [userId]);

  useEffect(() => {
    if (nomeGestorLogado) {
      setGestorExpandido(prev => ({
        ...prev,
        [nomeGestorLogado]: prev[nomeGestorLogado] !== undefined ? prev[nomeGestorLogado] : true,
      }));
    }
  }, [nomeGestorLogado]);

  useEffect(() => {
    const h = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setOpenMenuClienteId(null); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const filtrados = clientes.filter(c => {
    const t = searchTerm.toLowerCase();
    return (
      (!t || c.nome?.toLowerCase().includes(t) || c.email?.toLowerCase().includes(t)) &&
      (!filtroStatus || c.assinatura === filtroStatus) &&
      (!filtroPlano  || c.plano === filtroPlano)
    );
  });

  const grupos = {};
  if (filtrados.length > 0) grupos[nomeGestorLogado || "Sem Gestor"] = filtrados;

  const toggleGestor = (g) => setGestorExpandido(prev => ({ ...prev, [g]: !prev[g] }));

  const handleAbrirRelatorioEconomia = (cliente) => {
    navigate(`/extrato/${cliente.id}`, { state: { cliente, abaAtiva: "utilizacao" } });
  };

  const handleAbrirOperar = (cliente) => {
    navigate("/operar", { state: { cliente, compensacaoData: compensacaoData[cliente.id] } });
  };

  const hasCompensacao      = (id) => compensacaoData[id] && compensacaoData[id].length > 0;
  const getCompensacaoCount = (id) => compensacaoData[id]?.length || 0;

  const renderEconomia = (valor, assinatura, cliente) => {
    const isFinalizado = assinatura?.toLowerCase().includes("finalizado") || assinatura?.toLowerCase().includes("ciclo");
    if (isFinalizado) return (
      <span style={{ background: "#fff7e6", border: "1px solid #ffd591", color: "#d46b08", borderRadius: 4, padding: "3px 10px", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" }}>
        CICLO FINALIZADO
      </span>
    );
    return (
      <span onClick={() => handleAbrirRelatorioEconomia(cliente)}
        style={{ background: "#f6ffed", border: "1px solid #b7eb8f", color: "#389e0d", borderRadius: 4, padding: "3px 10px", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap", cursor: "pointer", transition: "all 0.2s", display: "inline-block" }}
        onMouseEnter={e => { e.currentTarget.style.background = "#d9f7be"; e.currentTarget.style.transform = "scale(1.02)"; }}
        onMouseLeave={e => { e.currentTarget.style.background = "#f6ffed"; e.currentTarget.style.transform = "scale(1)"; }}>
        R$ {Number(valor || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
      </span>
    );
  };

  // A última coluna (36px) é o menu "⋮", que só existe para gestor.
  // Para cliente, remove a coluna da grade — senão sobra um buraco vazio.
  const COLS = isCliente
    ? "2fr 2fr 1fr 1.6fr 1fr 190px"
    : "2fr 2fr 1fr 1.6fr 1fr 190px 36px";

  const ActionButtons = ({ c, full }) => (
    <div style={{ display: "flex", gap: 6, alignItems: "center", width: full ? "100%" : "auto" }}>
      <button onClick={() => navigate(`/extrato/${c.id}`, { state: { cliente: c } })}
        style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4, background: "transparent", border: `1px solid ${ORANGE}`, color: ORANGE, borderRadius: 4, padding: "6px 10px", fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap", flex: full ? 1 : "initial" }}
        onMouseEnter={e => { e.currentTarget.style.background = ORANGE; e.currentTarget.style.color = "#fff"; }}
        onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = ORANGE; }}>
        <FileTextOutlined style={{ fontSize: 11 }} /> EXTRATO
      </button>
      <button onClick={() => handleAbrirOperar(c)}
        style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4, background: "transparent", border: "1px solid #020a15", color: "#020a15", borderRadius: 4, padding: "6px 10px", fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap", position: "relative", flex: full ? 1 : "initial" }}
        onMouseEnter={e => { e.currentTarget.style.background = "#153766"; e.currentTarget.style.color = "#fff"; }}
        onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#0f213a"; }}>
        <ThunderboltOutlined style={{ fontSize: 11 }} /> OPERAR
        {hasCompensacao(c.id) && (
          <span style={{ position: "absolute", top: -6, right: -6, background: "#ff4d4f", color: "#fff", borderRadius: "50%", width: 18, height: 18, fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", border: "2px solid #fff" }}>
            {getCompensacaoCount(c.id)}
          </span>
        )}
      </button>
    </div>
  );

  return (
    <div>
      {clienteSelecionado && <ModalDetalhes cliente={clienteSelecionado} onClose={() => setClienteSelecionado(null)} />}
      {!isCliente && showNovoCliente && <ModalNovoCliente userId={userId} gestores={gestores} onClose={() => setShowNovoCliente(false)} onSuccess={carregarDados} />}
      {!isCliente && clienteEditar && <ModalEditarCliente cliente={clienteEditar} onClose={() => setClienteEditar(null)} onSuccess={carregarDados} />}

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <ShoppingCartOutlined style={{ fontSize: 18, color: "#555" }} />
          <span style={{ fontSize: 18, fontWeight: 600, color: "#222" }}>Carteira</span>
        </div>
        {/* 🔒 NOVO CLIENTE: exclusivo do gestor. */}
        {!isCliente && (
          <button onClick={abrirNovoCliente}
            style={{ display: "flex", alignItems: "center", gap: 6, background: DARK_NAV, color: "#fff", border: "none", borderRadius: 6, padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            <PlusOutlined style={{ fontSize: 12 }} /> NOVO CLIENTE
          </button>
        )}
      </div>

      {/* Filtros */}
      <div style={{ background: "#fff", borderRadius: 8, border: "1px solid #e8e8e8", marginBottom: 16, overflow: "hidden" }}>
        <div onClick={() => setFiltroAberto(v => !v)}
          style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: isMobile ? "12px 16px" : "14px 20px", cursor: "pointer", borderBottom: filtroAberto ? "1px solid #f0f0f0" : "none" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, fontWeight: 500, color: "#333" }}>
            <SearchOutlined /> Filtrar Clientes {!isMobile && "por Interesses"}
          </div>
          <DownOutlined style={{ color: "#aaa", fontSize: 12, transform: filtroAberto ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }} />
        </div>
        {filtroAberto && (
          <div style={{ padding: isMobile ? "14px 16px" : "16px 20px" }}>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 10 }}>
              <div style={{ flex: 1, minWidth: isMobile ? "100%" : 220 }}>
                <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>Buscar cliente</div>
                <Input placeholder="Nome ou e-mail..." prefix={<SearchOutlined style={{ color: "#bbb" }} />} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} allowClear />
              </div>
              <div style={{ minWidth: isMobile ? "100%" : 160, flex: isMobile ? "1 1 100%" : "initial" }}>
                <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>Status assinatura</div>
                <Select placeholder="Todos" allowClear style={{ width: "100%" }} onChange={setFiltroStatus} value={filtroStatus}>
                  <Select.Option value="ATIVA">Ativa</Select.Option>
                  <Select.Option value="INATIVA">Inativa</Select.Option>
                  <Select.Option value="CICLO FINALIZADO">Ciclo Finalizado</Select.Option>
                </Select>
              </div>
              <div style={{ minWidth: isMobile ? "100%" : 160, flex: isMobile ? "1 1 100%" : "initial" }}>
                <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>Plano</div>
                <Select placeholder="Todos" allowClear style={{ width: "100%" }} onChange={setFiltroPlano} value={filtroPlano}>
                  <Select.Option value="GESTAO">Gestão</Select.Option>
                  <Select.Option value="BASICO">Básico</Select.Option>
                  <Select.Option value="PREMIUM">Premium</Select.Option>
                </Select>
              </div>
            </div>
            <div style={{ fontSize: 13, color: "#888" }}>
              Exibindo {filtrados.length > 0 ? "todos os clientes." : "nenhum cliente."}
            </div>
          </div>
        )}
      </div>

      {/* Tabela / Cards */}
      <div style={{ background: "#fff", borderRadius: 8, border: "1px solid #e8e8e8", overflow: isMobile ? "visible" : "hidden" }}>
        {!isMobile && (
          <div style={{ display: "grid", gridTemplateColumns: COLS, padding: "10px 20px", background: "#fafafa", borderBottom: "1px solid #f0f0f0" }}>
            {["Gestor/Cliente", "Email", "Plano", "Economia Apurada", "Status\nAssinatura", "", ...(isCliente ? [] : [""])].map((h, i) => (
              <div key={i} style={{ fontSize: 12, fontWeight: 600, color: "#888", whiteSpace: "pre-line" }}>{h}</div>
            ))}
          </div>
        )}
        <Spin spinning={loading}>
          {Object.keys(grupos).length === 0 && !loading && (
            <div style={{ textAlign: "center", padding: "48px 0", color: "#bbb", fontSize: 14 }}>Nenhum cliente encontrado</div>
          )}
          {Object.entries(grupos).map(([gestor, membros]) => (
            <div key={gestor}>
              <div onClick={() => toggleGestor(gestor)}
                style={{ display: "flex", alignItems: "center", gap: 8, padding: isMobile ? "10px 16px" : "10px 20px", background: "#fafafa", borderBottom: "1px solid #f0f0f0", cursor: "pointer", fontWeight: 600, fontSize: 14, color: "#333" }}
                onMouseEnter={e => e.currentTarget.style.background = "#f0f4f7"}
                onMouseLeave={e => e.currentTarget.style.background = "#fafafa"}>
                <DownOutlined style={{ fontSize: 11, color: "#aaa", transform: gestorExpandido[gestor] ? "rotate(0deg)" : "rotate(-90deg)", transition: "transform 0.2s" }} />
                <UserOutlined style={{ color: "#aaa", fontSize: 14 }} />
                {gestor}
              </div>

              {gestorExpandido[gestor] && (isMobile ? (
                // ── Cards no mobile ──
                membros.map((c) => (
                  <div key={c.key} style={{ padding: "14px 16px", borderBottom: "1px solid #f5f5f5", position: "relative" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: "#333" }}>{c.nome}</div>
                        <div style={{ fontSize: 12, color: "#666", marginTop: 3, wordBreak: "break-all" }}>{c.email}</div>
                      </div>
                      {/* 🔒 O ícone "⋮" inteiro só existe para gestor.
                          Antes ele RENDERIZAVA para cliente e abria/fechava state,
                          mas o dropdown estava guardado por !isCliente — ou seja,
                          um botão que não fazia nada. */}
                      {!isCliente && (
                        <div style={{ cursor: "pointer", padding: 6, borderRadius: 4, background: openMenuClienteId === c.key ? "#f0f0f0" : "transparent", flexShrink: 0 }}
                          onClick={(e) => { e.stopPropagation(); setOpenMenuClienteId(openMenuClienteId === c.key ? null : c.key); }}>
                          <MoreOutlined style={{ fontSize: 18, color: "#555" }} />
                        </div>
                      )}
                      {!isCliente && openMenuClienteId === c.key && (
                        <div ref={menuRef} style={{ position: "absolute", right: 16, top: 40, background: "#fff", borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.15)", border: "1px solid #e8e8e8", zIndex: 300, minWidth: 140, overflow: "hidden" }}>
                          <button onClick={() => { setOpenMenuClienteId(null); abrirEditarCliente(c); }}
                            style={{ width: "100%", padding: "12px 16px", border: "none", borderBottom: "1px solid #f0f0f0", background: "#fff", textAlign: "left", fontSize: 14, color: "#333", cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}>
                            <span>✏️</span> Editar
                          </button>
                          <button onClick={() => { setOpenMenuClienteId(null); setClienteSelecionado(c); }}
                            style={{ width: "100%", padding: "12px 16px", border: "none", background: "#fff", textAlign: "left", fontSize: 14, color: "#333", cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}>
                            <span>📋</span> Detalhes
                          </button>
                        </div>
                      )}
                    </div>

                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginTop: 10 }}>
                      <span style={{ fontSize: 12, color: "#333", fontWeight: 500 }}>{c.plano}</span>
                      <span style={{ border: "1px solid #d9d9d9", borderRadius: 4, padding: "2px 8px", fontSize: 12, color: "#555", fontWeight: 500 }}>
                        {c.assinatura || "ATIVA"}
                      </span>
                      {renderEconomia(c.economiaApurada, c.assinatura, c)}
                    </div>

                    <div style={{ marginTop: 12 }}>
                      <ActionButtons c={c} full />
                    </div>
                  </div>
                ))
              ) : (
                // ── Grid no desktop ──
                membros.map((c, idx) => (
                  <div key={c.key}
                    style={{ display: "grid", gridTemplateColumns: COLS, padding: "12px 20px", borderBottom: "1px solid #f5f5f5", alignItems: "center", background: idx % 2 === 0 ? "#fff" : "#fafcff", position: "relative" }}
                    onMouseEnter={e => e.currentTarget.style.background = "#f0f7ff"}
                    onMouseLeave={e => e.currentTarget.style.background = idx % 2 === 0 ? "#fff" : "#fafcff"}>
                    <div style={{ fontSize: 13, color: "#333", paddingLeft: 28 }}>{c.nome}</div>
                    <div style={{ fontSize: 13, color: "#666", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.email}</div>
                    <div style={{ fontSize: 13, color: "#333", fontWeight: 500 }}>{c.plano}</div>
                    <div>{renderEconomia(c.economiaApurada, c.assinatura, c)}</div>
                    <div>
                      <span style={{ border: "1px solid #d9d9d9", borderRadius: 4, padding: "2px 8px", fontSize: 12, color: "#555", fontWeight: 500 }}>
                        {c.assinatura || "ATIVA"}
                      </span>
                    </div>
                    <ActionButtons c={c} />
                    {/* 🔒 Coluna do menu "⋮": não renderiza nada para cliente
                        (a própria coluna já foi removida de COLS). */}
                    {!isCliente && (
                      <div style={{ display: "flex", justifyContent: "center", position: "relative" }}>
                        <div style={{ cursor: "pointer", padding: "8px", borderRadius: "4px", display: "flex", alignItems: "center", justifyContent: "center", background: openMenuClienteId === c.key ? "#f0f0f0" : "transparent" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (openMenuClienteId === c.key) {
                              setOpenMenuClienteId(null);
                            } else {
                              const r = e.currentTarget.getBoundingClientRect();
                              setMenuPos({ top: r.bottom + 4, left: r.right - 140 });
                              setOpenMenuClienteId(c.key);
                            }
                          }}>
                          <MoreOutlined style={{ fontSize: 18, color: "#555" }} />
                        </div>
                        {openMenuClienteId === c.key && (
                          <div ref={menuRef} style={{ position: "fixed", top: menuPos.top, left: menuPos.left, background: "#fff", borderRadius: "8px", boxShadow: "0 4px 12px rgba(0,0,0,0.15)", border: "1px solid #e8e8e8", zIndex: 9999, minWidth: "140px", overflow: "hidden" }}>
                            <button onClick={() => { setOpenMenuClienteId(null); abrirEditarCliente(c); }}
                              style={{ width: "100%", padding: "12px 16px", border: "none", borderBottom: "1px solid #f0f0f0", background: "#fff", textAlign: "left", fontSize: "14px", color: "#333", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px" }}
                              onMouseEnter={e => e.currentTarget.style.background = "#f5f5f5"}
                              onMouseLeave={e => e.currentTarget.style.background = "#fff"}>
                              <span>✏️</span> Editar
                            </button>
                            <button onClick={() => { setOpenMenuClienteId(null); setClienteSelecionado(c); }}
                              style={{ width: "100%", padding: "12px 16px", border: "none", background: "#fff", textAlign: "left", fontSize: "14px", color: "#333", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px" }}
                              onMouseEnter={e => e.currentTarget.style.background = "#f5f5f5"}
                              onMouseLeave={e => e.currentTarget.style.background = "#fff"}>
                              <span>📋</span> Detalhes
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))
              ))}
            </div>
          ))}
        </Spin>
      </div>
    </div>
  );
}

// ─── DASHBOARD PRINCIPAL ──────────────────────────────────────────────────────
export default function Dashboard() {
  const navigate                      = useNavigate();
  const location                      = useLocation();
  const isMobile                      = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(() => !isMobile);
  const [currentPage, setCurrentPage] = useState(() => location.state?.currentPage || "carteira");
  const [userName, setUserName]       = useState("");
  const [userRole, setUserRole]       = useState("");
  const [userId, setUserId]           = useState(null);

  // 🔒 Lê JÁ no primeiro render (initializer do useState).
  // Antes começava em `false` e só corrigia dentro do useEffect — resultado:
  // o botão "NOVO CLIENTE" PISCAVA visível para o cliente antes de sumir.
  const [isCliente, setIsCliente]     = useState(lerIsCliente);

  const notif = useCheckinNotificacoes();
  const notifPassaporte = usePassaporteNotificacoes(userId);
  const notifTransferencia = useTransferenciaNotificacoes();

  useEffect(() => {
    const id   = localStorage.getItem("userID");
    const name = localStorage.getItem("userName");
    const role = localStorage.getItem("userRole") || "coordenador";
    const cliente = lerIsCliente();

    // 🔎 DIAGNÓSTICO — remova depois de confirmar.
    // Se o botão NOVO CLIENTE aparecer para um cliente, olhe este log:
    // ele mostra o valor CRU do localStorage. Se vier null/"0"/"false" para
    // alguém que é cliente, o bug está na tela de LOGIN, não aqui.
    console.log("[Auth] userCliente =", localStorage.getItem("userCliente"), "→ isCliente =", cliente);

    if (!id) { navigate("/login"); return; }

    setUserId(id);
    setUserName(name || "Usuário");
    setUserRole(role);
    setIsCliente(cliente);

    if (cliente) {
      setCurrentPage("carteira");
    } else {
      setCurrentPage(location.state?.currentPage || "gestores");
    }
  }, [navigate, location.state]);


  useEffect(() => {
    setSidebarOpen(!isMobile);
  }, [isMobile]);

  const handlePageChange = (page) => {
    // Allowlist: só o que está em PAGINAS_CLIENTE é liberado para o cliente.
    if (isCliente && !PAGINAS_CLIENTE.includes(page)) {
      message.warning("Você não tem acesso a essa página");
      return;
    }
    setCurrentPage(page);
  };

  const renderPage = () => {
    // 🔒 Segunda barreira: mesmo que currentPage seja forçado por
    // location.state (navegação programática vinda de outra tela), o cliente
    // nunca renderiza uma página fora da allowlist.
    const pagina = (isCliente && !PAGINAS_CLIENTE.includes(currentPage))
      ? "carteira"
      : currentPage;

    switch (pagina) {
      case "gestores":      return <GestoresPage userId={userId} />;
      case "carteira":      return <CarteiraPage userId={userId} isCliente={isCliente} />;
      case "dados-empresa": return <Empresa clienteId={userId} />;
      case "notificacoes":  return <Notificacoes controller={notif} />;
      case "tarefas":       return <Tarefas userId={userId} />;
      case "passaporte":    return <Passaporte userId={userId} />;
      case "promocoes":     return <Promocoes userId={userId} />;
      case "bloqueios":     return <Bloqueio userId={userId} />;
      case "VooHoteis": {
        // APENAS para Voos e Hotéis: usa o cli_id quando for cliente.
        // ⚠️ Note que a chave é "userIdCliente" — diferente de "userCliente"
        //    (a flag de perfil). São duas chaves distintas, não confunda.
        const idCliente = isCliente ? localStorage.getItem("userIdCliente") : userId;
        return <VooHotel userId={idCliente} isCliente={isCliente} />;
      }
      case "alertas":       return <NotificacoesTransferencia controller={notifTransferencia} />;
      default:              return isCliente
        ? <CarteiraPage userId={userId} isCliente={isCliente} />
        : <GestoresPage userId={userId} />;
    }
  };

  const notifBadge = {
    notificacoes: {
      count: notif.total,
      color: notif.totalUrgente > 0 ? "#ff4d4f" : "#1677ff",
    },
    passaporte: {
      count: notifPassaporte.total,
      color: notifPassaporte.totalUrgente > 0 ? "#ff4d4f" : "#fa8c16",
    },
    alertas: {
      count: notifTransferencia.total,
      color: notifTransferencia.total > 0 ? "#ff4d4f" : "#722ed1",
    },
  };

  const contentMarginLeft = isMobile ? 0 : (sidebarOpen ? SIDEBAR_W : 0);

  return (
    <div style={{ minHeight: "100vh", background: "#f0f2f5", overflowX: "hidden", maxWidth: "100vw" }}>
      <Topbar userName={userName} userRole={userRole} onMenuToggle={() => setSidebarOpen(v => !v)} />
      <Sidebar
        current={currentPage}
        onChange={handlePageChange}
        visible={sidebarOpen}
        badges={notifBadge}
        isCliente={isCliente}
        isMobile={isMobile}
        onRequestClose={() => setSidebarOpen(false)}
      />
      <div style={{
        marginLeft: contentMarginLeft,
        marginTop: 56,
        padding: isMobile ? "16px 12px" : "28px 32px",
        transition: "margin-left 0.25s ease",
        minHeight: "calc(100vh - 56px)",
        boxSizing: "border-box",
        overflowX: "hidden",
      }}>
        {renderPage()}
      </div>
    </div>
  );
}